import { Clock, Play, Star, Users } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { InlineEdit } from '@/components/recipe/inline-edit'
import type { Recipe } from '@/types/recipe'
import { DifficultyIndicator } from '@/components/recipe/difficulty-indicator'

interface RecipeDetailHeaderProps {
  recipe: Recipe
  isInlineEditMode: boolean
  totalTime: number
  onStartCookingMode: () => void
  onInlineUpdate: (field: string, value: unknown) => Promise<boolean>
}

export function RecipeDetailHeader({
  recipe,
  isInlineEditMode,
  totalTime,
  onStartCookingMode,
  onInlineUpdate,
}: RecipeDetailHeaderProps) {
  return (
    <div>
      {recipe.image_url && (
        <div className="mb-6 aspect-video w-full overflow-hidden rounded-lg">
          <img src={recipe.image_url} alt={recipe.title} className="h-full w-full object-cover" />
        </div>
      )}

      <h1 className="mb-4 text-3xl font-bold text-gray-900 sm:text-4xl">
        {isInlineEditMode ? (
          <InlineEdit
            value={recipe.title}
            onSave={(value) => onInlineUpdate('title', value)}
            placeholder="Recept titel..."
            className="text-4xl font-bold text-gray-900"
          />
        ) : (
          recipe.title
        )}
      </h1>

      {recipe.description && (
        <p className="mb-6 text-base text-gray-600 sm:text-lg">
          {isInlineEditMode ? (
            <InlineEdit
              value={recipe.description}
              onSave={(value) => onInlineUpdate('description', value)}
              placeholder="Beschrijving..."
              multiline
              className="text-lg text-gray-600"
            />
          ) : (
            recipe.description
          )}
        </p>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-4">
        {totalTime > 0 && (
          <div className="flex items-center text-gray-600">
            <Clock className="mr-2 h-5 w-5" />
            <span className="font-medium">{totalTime} min</span>
          </div>
        )}

        <div className="flex items-center text-gray-600">
          <Users className="mr-2 h-5 w-5" />
          <span className="font-medium">
            {recipe.servings ? `${recipe.servings} porties` : 'Porties niet ingesteld'}
          </span>
        </div>

        <DifficultyIndicator difficulty={recipe.difficulty} />

        <div className="flex items-center text-gray-600">
          <Star className="mr-1 h-5 w-5 fill-yellow-400 text-yellow-400" />
          <span className="font-medium">{recipe.rating ? `${recipe.rating}/5` : 'Geen beoordeling'}</span>
        </div>
      </div>

      {recipe.tags.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {recipe.tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {!isInlineEditMode && recipe.instructions.length > 0 && (
        <Button onClick={onStartCookingMode} size="lg">
          <Play className="mr-2 h-5 w-5" />
          Start Kookmodus
        </Button>
      )}
    </div>
  )
}
